import React from "react";
import { Platform } from "react-native";
import Svg, {
  Circle,
  Ellipse,
  Line,
  Path,
  Polygon,
  Polyline,
  Rect,
} from "react-native-svg";
import { typography } from "@heraion/shared";
import { ICON_GEOMETRY, type IconElement, type IconName } from "./iconData";

export type { IconName } from "./iconData";

// The one stroke-based icon family (DESIGN.md Decision 2). Drawn with
// react-native-svg so it is identical on mobile and web-rnw, takes a token
// colour, and is sized off the type scale — so an icon next to a label matches
// its cap height and a utility icon is `ink2`, never the brand (it obeys the
// colour budget automatically). Replaces emoji-as-icons, which render per-device
// and can't take a colour or a precise size.
//
// Geometry is Lucide (https://lucide.dev, ISC license, (c) Lucide Contributors),
// generated into ./iconData.ts. stroke/fill/size live here on the root <Svg>;
// never per element.

const TAG_COMPONENTS: Record<string, React.ComponentType<any>> = {
  path: Path,
  circle: Circle,
  line: Line,
  polyline: Polyline,
  polygon: Polygon,
  rect: Rect,
  ellipse: Ellipse,
};

export type IconProps = {
  name: IconName;
  /** px. Defaults to the body size (text-base, 16) so it matches inline text. */
  size?: number;
  /** Any token colour — caller passes ink/ink2/ink3/status. Defaults inherit. */
  color?: string;
  /** Stroke weight; 2 is the Lucide default and the DESIGN.md icon weight. */
  strokeWidth?: number;
  /** Fill colour for solid-state icons (e.g. a favourited ★). Default none (outline). */
  fill?: string;
  /** a11y label; omit to mark the icon decorative (most cases — text sits beside it). */
  label?: string;
};

function renderElement([tag, attrs]: IconElement, key: number) {
  const Comp = TAG_COMPONENTS[tag];
  if (!Comp) return null;
  return <Comp key={key} {...attrs} />;
}

export function Icon({
  name,
  size = typography.sizes.base,
  color = "currentColor",
  strokeWidth = 2,
  fill = "none",
  label,
}: IconProps) {
  const geometry = ICON_GEOMETRY[name];
  return (
    <Svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      // `accessible` is what actually registers the node with the OS
      // accessibility tree — a label alone on a non-touchable does not, so a
      // labelled icon was silently unreachable on iOS (found reviewing IB-11:
      // the icon-only PR/MAX badges have nothing BUT this label).
      accessible={!!label}
      accessibilityLabel={label}
      accessibilityRole={label ? "image" : undefined}
      // Decorative by default: text labels sit beside icons throughout the app,
      // so the icon must not double-announce. Pass `label` to expose it. The
      // hide hint is platform-split (aria-hidden is web-only; on native an
      // unlabelled svg is non-focusable already).
      {...(!label && Platform.OS === "web" ? { "aria-hidden": true } : {})}
    >
      {geometry.map(renderElement)}
    </Svg>
  );
}
